import React, { useState, useEffect, useCallback } from 'react';
import { fitnessApi } from '../api';

interface EpData {
  ep: number;
  ep_max: number;
  base_ep: number;
  streak_bonus: number;
  energy_bonus: number;
  mood_bonus?: number;
  decay: number;
  energy_status?: {
    level: string;
    label: string;
    icon: string;
    color: string;
    suggestion: string;
  };
}

interface TaskItem {
  id: number;
  task_type: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  exp_reward: number;
  status: 'pending' | 'completed' | 'expired';
  assigned_date: string;
  completed_at: string | null;
}

interface Props {
  onClose: () => void;
}

interface HeatmapDay {
  date: string;
  ep_earned: number;
  tasks_done: number;
  has_mood: boolean;
}

interface HeatmapData {
  days: HeatmapDay[];
  summary: {
    total_active_days: number;
    longest_streak: number;
    current_streak: number;
    start_date: string;
    end_date: string;
    weeks: number;
  };
}

const CATEGORY_COLORS: Record<string, string> = {
  '睡眠管理': '#8b5cf6',
  '正念冥想': '#06b6d4',
  '专注管理': '#f59e0b',
  '情绪调节': '#f43f5e',
  '恢复充电': '#10b981',
  '社交能量': '#ec4899',
};

const EnergyCenter: React.FC<Props> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState<number | null>(null);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [epData, setEpData] = useState<EpData | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [todayRes, summaryRes, heatmapRes] = await Promise.all([
        fitnessApi.getTodayTasks(),
        fitnessApi.getSummary(),
        fitnessApi.getEnergyHeatmap(),
      ]);
      const allTasks: TaskItem[] = todayRes.data?.tasks || [];
      setTasks(allTasks.filter(t => t.task_type === 'energy'));
      setEpData(summaryRes.data?.ep || null);
      setStats(summaryRes.data?.stats || null);
      setHeatmapData(heatmapRes.data || null);
    } catch (err) {
      console.error('加载精力数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleComplete = async (taskId: number) => {
    try {
      setCompleting(taskId);
      await fitnessApi.completeTask(taskId);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || '完成失败');
    } finally {
      setCompleting(null);
    }
  };

  const ep = epData?.ep ?? 0;
  const epMax = epData?.ep_max ?? 100;
  const epPct = Math.min(100, Math.round((ep / epMax) * 100));

  const filteredTasks = tasks.filter(t => {
    if (filter === 'pending') return t.status === 'pending';
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  const grouped = filteredTasks.reduce<Record<string, TaskItem[]>>((acc, t) => {
    const cat = t.category || '其他';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  return (
    <div className="ec-root">
      <header className="ec-header">
        <button className="ec-back" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h1 className="ec-header-top">⚡ 精力管理</h1>
        <div style={{ width: 34, flexShrink: 0 }} />
      </header>

      <main className="ec-main">
        {loading ? (
          <div className="ec-loading"><div className="ec-spinner" /><p>加载中...</p></div>
        ) : (
          <>
            {/* EP 仪表盘 */}
            <div className="ec-dashboard">
              <div className="ec-gauge">
                <svg viewBox="0 0 120 120" className="ec-gauge-svg">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(30,41,59,0.8)" strokeWidth="8" />
                  <circle cx="60" cy="60" r="52" fill="none" stroke="url(#epGrad)" strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 52}`}
                    strokeDashoffset={`${2 * Math.PI * 52 * (1 - epPct / 100)}`}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '60px 60px', transition: 'stroke-dashoffset 1s ease' }}
                  />
                  <defs>
                    <linearGradient id="epGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#1d4ed8" />
                      <stop offset="100%" stopColor="#60a5fa" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="ec-gauge-center">
                  {epData?.energy_status ? (
                    <>
                      <span className="ec-gauge-icon">{epData.energy_status.icon}</span>
                      <span className="ec-gauge-value" style={{ fontSize: '24px' }}>{ep}</span>
                    </>
                  ) : (
                    <>
                      <span className="ec-gauge-value">{ep}</span>
                      <span className="ec-gauge-label">EP</span>
                    </>
                  )}
                </div>
              </div>

              <div className="ec-breakdown">
                <div className="ec-bd-row">
                  <span className="ec-bd-label">基础 EP</span>
                  <span className="ec-bd-val">{epData?.base_ep ?? 0}</span>
                </div>
                <div className="ec-bd-row">
                  <span className="ec-bd-label">连续加成</span>
                  <span className="ec-bd-val ec-pos">+{epData?.streak_bonus ?? 0}</span>
                </div>
                <div className="ec-bd-row">
                  <span className="ec-bd-label">今日精力</span>
                  <span className="ec-bd-val ec-pos">+{epData?.energy_bonus ?? 0}</span>
                </div>
                <div className="ec-bd-row">
                  <span className="ec-bd-label">累计精力经验</span>
                  <span className="ec-bd-val" style={{ color: 'var(--web-chart-text, #93c5fd)' }}>{stats?.total_ep_earned ?? 0}</span>
                </div>
                <div className="ec-bd-row">
                  <span className="ec-bd-label">不活跃衰减</span>
                  <span className="ec-bd-val ec-neg">-{epData?.decay ?? 0}</span>
                </div>
              </div>
            </div>

            {/* EP 能量状态 */}
            {epData?.energy_status && (
              <div className="ec-status-banner" style={{
                borderColor: `${epData.energy_status.color}30`,
                background: `linear-gradient(135deg, ${epData.energy_status.color}10, transparent)`,
              }}>
                <span className="ec-status-icon">{epData.energy_status.icon}</span>
                <div className="ec-status-text">
                  <span className="ec-status-label" style={{ color: epData.energy_status.color }}>
                    {epData.energy_status.label}
                  </span>
                  <span className="ec-status-suggestion">{epData.energy_status.suggestion}</span>
                </div>
              </div>
            )}

            {/* 统计卡片 */}
            <div className="ec-stats-row">
              <div className="ec-stat-card">
                <span className="ec-stat-num" style={{ color: 'var(--web-chart-text, #60a5fa)' }}>{stats?.total_energy_completed ?? 0}</span>
                <span className="ec-stat-label">已完成</span>
              </div>
              <div className="ec-stat-card">
                <span className="ec-stat-num" style={{ color: '#22c55e' }}>{stats?.energy_streak ?? 0}</span>
                <span className="ec-stat-label">连续天数</span>
              </div>
              <div className="ec-stat-card">
                <span className="ec-stat-num" style={{ color: '#f59e0b' }}>{stats?.total_ep_earned ?? 0}</span>
                <span className="ec-stat-label">累计 EP</span>
              </div>
            </div>

            {/* 精力热力图 */}
            {heatmapData && heatmapData.days.length > 0 && (() => {
              const days = heatmapData.days;
              const startDow = (new Date(days[0].date).getDay() + 6) % 7;
              const padded: (HeatmapDay | null)[] = Array(startDow).fill(null).concat(days);
              while (padded.length % 7 !== 0) padded.push(null);
              const numWeeks = padded.length / 7;
              const weekCols: (HeatmapDay | null)[][] = [];
              for (let w = 0; w < numWeeks; w++) weekCols.push(padded.slice(w * 7, w * 7 + 7));

              const getColor = (ep: number) => {
                if (ep <= 0) return 'var(--web-heat-0, rgba(30,41,59,0.6))';
                if (ep < 10) return 'var(--web-heat-1, rgba(96,165,250,0.2))';
                if (ep < 20) return 'var(--web-heat-2, rgba(96,165,250,0.4))';
                if (ep < 30) return 'var(--web-heat-3, rgba(96,165,250,0.65))';
                return 'var(--web-heat-4, rgba(96,165,250,0.9))';
              };

              const dayLabels = ['一', '', '三', '', '五', '', '日'];
              const last7 = days.slice(-7);
              const maxEp7 = Math.max(...last7.map(d => d.ep_earned), 1);

              return (
                <div className="ec-heatmap-section">
                  <div className="ec-heatmap-header">
                    <span className="ec-heatmap-title">精力节律</span>
                    <div className="ec-heatmap-stats">
                      <span>活跃 <b>{heatmapData.summary.total_active_days}</b> 天</span>
                      <span>最长 <b>{heatmapData.summary.longest_streak}</b> 天</span>
                    </div>
                  </div>
                  <div className="ec-heatmap-wrap">
                    <div className="ec-heatmap-labels">
                      {dayLabels.map((l, i) => <span key={i} className="ec-heatmap-dlabel">{l}</span>)}
                    </div>
                    <div className="ec-heatmap-grid" style={{ gridTemplateColumns: `repeat(${numWeeks}, 1fr)` }}>
                      {weekCols.map((week, wi) =>
                        week.map((day, di) => (
                          <div
                            key={`${wi}-${di}`}
                            className="ec-heatmap-cell"
                            style={{ background: day ? getColor(day.ep_earned) : 'transparent' }}
                            title={day ? `${day.date}: ${day.ep_earned} EP` : ''}
                          >
                            {day?.has_mood && <span className="ec-heatmap-mood" />}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="ec-heatmap-legend">
                    <span className="ec-heatmap-legend-label">少</span>
                    {[0, 5, 15, 25, 35].map(v => (
                      <span key={v} className="ec-heatmap-legend-box" style={{ background: getColor(v) }} />
                    ))}
                    <span className="ec-heatmap-legend-label">多</span>
                  </div>
                  <div className="ec-trend-section">
                    <span className="ec-trend-title">近 7 天趋势</span>
                    <div className="ec-trend-bars">
                      {last7.map((d, i) => (
                        <div key={i} className="ec-trend-col">
                          <div className="ec-trend-bar-wrap">
                            <div
                              className="ec-trend-bar"
                              style={{
                                height: `${Math.max(4, (d.ep_earned / maxEp7) * 100)}%`,
                                background: d.ep_earned > 0 ? 'var(--web-heat-4, #60a5fa)' : 'var(--web-heat-0, rgba(30,41,59,0.6))',
                              }}
                            />
                          </div>
                          <span className="ec-trend-day">
                            {['日', '一', '二', '三', '四', '五', '六'][new Date(d.date).getDay()]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* 筛选条 */}
            <div className="ec-filter-bar">
              <button className={`ec-filter ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
                全部 ({tasks.length})
              </button>
              <button className={`ec-filter ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
                待完成 ({pendingCount})
              </button>
              <button className={`ec-filter ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>
                已完成 ({completedCount})
              </button>
            </div>

            {/* 按类别分组的任务列表 */}
            {Object.keys(grouped).length === 0 ? (
              <div className="ec-empty">
                <p>⚡ 本周暂无精力任务</p>
                <p className="ec-empty-sub">系统每周一自动生成新的精力管理任务</p>
              </div>
            ) : (
              Object.entries(grouped).map(([cat, catTasks]) => {
                const color = CATEGORY_COLORS[cat] || '#64748b';
                return (
                  <div key={cat} className="ec-cat-section">
                    <div className="ec-cat-header">
                      <span className="ec-cat-dot" style={{ background: color }} />
                      <span className="ec-cat-name">{cat}</span>
                      <span className="ec-cat-count">{catTasks.filter(t => t.status === 'completed').length}/{catTasks.length}</span>
                    </div>
                    {catTasks.map(t => (
                      <div key={t.id} className={`ec-task ${t.status === 'completed' ? 'done' : ''}`}>
                        <div className="ec-task-left">
                          {t.status === 'completed' ? (
                            <div className="ec-check-done" style={{ background: `${color}30`, color }}>✓</div>
                          ) : (
                            <button
                              className="ec-check-btn"
                              style={{ borderColor: `${color}60` }}
                              onClick={() => handleComplete(t.id)}
                              disabled={completing !== null}
                            >
                              {completing === t.id ? '…' : ''}
                            </button>
                          )}
                        </div>
                        <div className={`ec-task-body ${t.status === 'completed' ? 'faded' : ''}`}>
                          <div className="ec-task-top">
                            <span className={`ec-task-title ${t.status === 'completed' ? 'struck' : ''}`}>
                              {t.icon} {t.title}
                            </span>
                            <span className="ec-task-exp" style={{ background: `${color}15`, color }}>
                              +{t.exp_reward} EP
                            </span>
                          </div>
                          {t.description && <p className="ec-task-desc">{t.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}

            {/* 公式说明 */}
            <div className="ec-formula">
              <h4>⚡ EP 计算公式</h4>
              <p><code>EP = 基础EP + 连续加成 + 今日精力 − 衰减</code></p>
              <ul>
                <li>基础 EP：累计精力经验 ÷ 10（上限 50）。累计值含「精力任务」奖励与「情绪」笔记（每条 +10）</li>
                <li>连续加成：连续 ≥3 天 +10，≥7 天再 +10（写日记也算精力活跃日）</li>
                <li>今日精力：当天完成过精力任务 → +15</li>
                <li>不活跃衰减：每不活跃 1 天 → −5</li>
              </ul>
            </div>
          </>
        )}
      </main>

      <style>{`
        .ec-root {
          min-height: 100vh;
          background-color: #030712;
          background-image: radial-gradient(circle at 50% 0%, #1e1b4b 0%, #030712 40%);
          color: #f8fafc;
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }

        .ec-header {
          min-height: 72px;
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          padding: calc(12px + var(--safe-area-inset-top, 0px)) 16px 10px;
          position: sticky; top: 0; z-index: 20;
          background: linear-gradient(180deg, rgba(3,7,18,0.95) 0%, rgba(3,7,18,0) 100%);
        }
        .ec-header-top {
          font-size: 17px; font-weight: 800; color: #f1f5f9; margin: 0; text-align: center; flex: 1;
        }
        .ec-back {
          flex-shrink: 0;
          width: 34px; height: 34px; border-radius: 50%;
          border: 1px solid rgba(148,163,184,0.15);
          background: rgba(15,23,42,0.6); color: #cbd5e1;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; -webkit-tap-highlight-color: transparent;
        }
        .ec-back:active { transform: scale(0.9); }

        .ec-main { padding: 12px 16px 100px; max-width: 640px; margin: 0 auto; }

        .ec-loading { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; }
        .ec-spinner { width: 32px; height: 32px; border: 3px solid rgba(51,65,85,0.5); border-top-color: #60a5fa; border-radius: 50%; animation: ecSpin 0.8s linear infinite; }
        @keyframes ecSpin { to { transform: rotate(360deg); } }

        /* Dashboard */
        .ec-dashboard {
          display: flex; align-items: center; gap: 24px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px; padding: 20px; margin-bottom: 16px;
        }
        .ec-gauge { position: relative; width: 120px; height: 120px; flex-shrink: 0; }
        .ec-gauge-svg { width: 100%; height: 100%; }
        .ec-gauge-center {
          position: absolute; inset: 0;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
        }
        .ec-gauge-value { font-size: 32px; font-weight: 800; color: #60a5fa; }
        .ec-gauge-label { font-size: 11px; color: rgba(148,163,184,0.6); font-weight: 600; letter-spacing: 1px; }

        .ec-breakdown { flex: 1; }
        .ec-bd-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 5px 0; font-size: 12px;
        }
        .ec-bd-label { color: rgba(148,163,184,0.7); }
        .ec-bd-val { font-weight: 700; color: rgba(226,232,240,0.9); }
        .ec-bd-val.ec-pos { color: #22c55e; }
        .ec-bd-val.ec-neg { color: #ef4444; }

        /* Stats */
        .ec-stats-row {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
          margin-bottom: 16px;
        }
        .ec-stat-card {
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px; padding: 14px; text-align: center;
        }
        .ec-stat-num { display: block; font-size: 22px; font-weight: 800; }
        .ec-stat-label { display: block; font-size: 11px; color: rgba(148,163,184,0.6); margin-top: 4px; }

        /* Filter */
        .ec-filter-bar {
          display: flex; gap: 6px; margin-bottom: 16px;
        }
        .ec-filter {
          padding: 7px 14px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 8px; background: rgba(15,23,42,0.6);
          color: rgba(148,163,184,0.7); font-size: 12px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .ec-filter.active {
          background: rgba(96,165,250,0.15); color: #60a5fa;
          border-color: rgba(96,165,250,0.4);
        }

        /* Category */
        .ec-cat-section { margin-bottom: 20px; }
        .ec-cat-header {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 10px; padding-left: 4px;
        }
        .ec-cat-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .ec-cat-name { font-size: 14px; font-weight: 700; color: rgba(226,232,240,0.9); }
        .ec-cat-count { font-size: 11px; color: rgba(148,163,184,0.5); margin-left: auto; }

        /* Task */
        .ec-task {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px; margin-bottom: 8px;
          background: rgba(17,24,39,0.7); backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px; transition: all 0.15s;
        }
        .ec-task.done { opacity: 0.55; }
        .ec-task-left { padding-top: 2px; flex-shrink: 0; }
        .ec-check-btn {
          width: 22px; height: 22px; border-radius: 6px;
          border: 2px solid rgba(148,163,184,0.3);
          background: rgba(15,23,42,0.5);
          cursor: pointer; color: transparent; font-size: 10px;
          transition: all 0.15s;
        }
        .ec-check-btn:active { transform: scale(0.9); }
        .ec-check-done {
          width: 22px; height: 22px; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700;
        }
        .ec-task-body { flex: 1; min-width: 0; }
        .ec-task-body.faded { opacity: 0.6; }
        .ec-task-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
        .ec-task-title { font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.95); }
        .ec-task-title.struck { text-decoration: line-through; }
        .ec-task-exp {
          font-size: 9px; font-weight: 700; white-space: nowrap;
          padding: 3px 8px; border-radius: 4px; flex-shrink: 0;
        }
        .ec-task-desc {
          font-size: 11px; color: rgba(148,163,184,0.6);
          line-height: 1.5; margin-top: 4px;
        }

        /* Empty */
        .ec-empty {
          padding: 40px 20px; text-align: center;
          color: rgba(148,163,184,0.5); font-size: 14px;
        }
        .ec-empty-sub { font-size: 12px; margin-top: 8px; }

        /* Formula */
        .ec-formula {
          margin-top: 20px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px; padding: 18px;
          font-size: 12px; color: rgba(148,163,184,0.6); line-height: 1.8;
        }
        .ec-formula h4 { color: rgba(226,232,240,0.9); margin: 0 0 8px; font-size: 14px; }
        .ec-formula code {
          background: rgba(0,0,0,.3); padding: 2px 8px; border-radius: 4px;
          color: #60a5fa; font-size: 11px;
        }
        .ec-formula ul { margin: 8px 0 0; padding-left: 18px; }
        .ec-formula li { margin-bottom: 2px; }

        /* Status Banner */
        .ec-status-banner {
          display: flex; align-items: center; gap: 14px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px; padding: 14px 16px; margin-bottom: 16px;
        }
        .ec-status-icon { font-size: 28px; flex-shrink: 0; }
        .ec-status-text { display: flex; flex-direction: column; gap: 4px; }
        .ec-status-label { font-size: 15px; font-weight: 700; }
        .ec-status-suggestion { font-size: 12px; color: rgba(148,163,184,0.7); line-height: 1.5; }
        .ec-gauge-icon { font-size: 20px; margin-bottom: 2px; }

        /* Heatmap */
        .ec-heatmap-section {
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px; padding: 16px; margin-bottom: 16px;
        }
        .ec-heatmap-header {
          display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
        }
        .ec-heatmap-title { font-size: 14px; font-weight: 700; color: rgba(226,232,240,0.9); }
        .ec-heatmap-stats {
          display: flex; gap: 12px; font-size: 11px; color: rgba(148,163,184,0.6);
        }
        .ec-heatmap-stats b { color: #60a5fa; font-weight: 700; }
        .ec-heatmap-wrap {
          display: flex; gap: 4px; overflow-x: auto;
          -webkit-overflow-scrolling: touch; padding-bottom: 4px;
        }
        .ec-heatmap-labels {
          display: flex; flex-direction: column; gap: 2px; flex-shrink: 0; padding-top: 0;
        }
        .ec-heatmap-dlabel {
          width: 14px; height: 11px; font-size: 9px; line-height: 11px;
          color: rgba(148,163,184,0.4); text-align: center;
        }
        .ec-heatmap-grid {
          display: grid; gap: 2px; flex: 1; min-width: 0;
          grid-auto-flow: column;
          grid-template-rows: repeat(7, 11px);
        }
        .ec-heatmap-cell {
          width: 11px; height: 11px; border-radius: 2px; position: relative;
        }
        .ec-heatmap-mood {
          position: absolute; bottom: -1px; right: -1px;
          width: 4px; height: 4px; border-radius: 50%; background: #f43f5e;
        }
        .ec-heatmap-legend {
          display: flex; align-items: center; gap: 3px;
          justify-content: flex-end; margin-top: 8px;
        }
        .ec-heatmap-legend-label { font-size: 9px; color: rgba(148,163,184,0.4); margin: 0 2px; }
        .ec-heatmap-legend-box { width: 10px; height: 10px; border-radius: 2px; }

        /* 7-day trend */
        .ec-trend-section {
          margin-top: 14px; padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .ec-trend-title { font-size: 12px; color: rgba(148,163,184,0.5); font-weight: 600; }
        .ec-trend-bars {
          display: flex; gap: 6px; margin-top: 8px; align-items: flex-end; height: 48px;
        }
        .ec-trend-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
        .ec-trend-bar-wrap {
          width: 100%; height: 40px; display: flex; align-items: flex-end; justify-content: center;
        }
        .ec-trend-bar { width: 100%; max-width: 24px; border-radius: 3px 3px 0 0; transition: height 0.5s ease; }
        .ec-trend-day { font-size: 9px; color: rgba(148,163,184,0.4); }
      `}</style>
    </div>
  );
};

export default EnergyCenter;
